import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { builderSessionId, useMetadataBuilderStore } from "../../stores/metadata-builder-store";
import { useMetadataHistoryStore } from "../../stores/metadata-history-store";
import { useMetadataEditingStore } from "../../stores/metadata-editing-store";
import { weekdayKeys } from "../../lib/schedule-time/schedule-dates";
import { createOneSlotLayout, dayDateEnabled, setDayDateFormat } from "../../lib/metadata-builder/one-slot-layout";
import { dispatchBuilderMetadata } from "../../lib/metadata-builder/edit-text";
import { endMetadataMerge } from "../../lib/metadata-history/dispatch";
import { metadataLabels } from "../editor-panel/developer-section/metadata-form-schema";
import { DayPreviewControls } from "./DayPreviewControls";
import { DayAnchorControls } from "./DayAnchorControls";
import { DayLayoutExceptionControls } from "./DayLayoutExceptionControls";
import { StreamElementEditor } from "./StreamElementEditor";
import { createTwoSlotLayout, copyStreamLayout } from "../../lib/metadata-builder/two-slot-layout";
import { streamBindings, requiredStreamBindings, streamLayoutComplete, toggleStreamElement, type StreamNumber } from "../../lib/metadata-builder/stream-layout";
import type { StreamLayout } from "../../lib/metadata-builder/day-layouts";
import { DayDateEditor } from "./DayDateEditor";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { BuilderStreamInputs } from "./BuilderStreamInputs";

type StreamStepProps = { mode: StreamLayout["mode"] };

export function StreamStep({ mode }: StreamStepProps) {
  const { t } = useTranslation();
  const [selectedStream, setSelectedStream] = useState<StreamNumber>(1);
  const twoStreams = mode === "two_slot";
  const createLayout = twoStreams ? createTwoSlotLayout : createOneSlotLayout;
  const session = useMetadataHistoryStore(state => state.sessions[builderSessionId]);
  const day = useMetadataBuilderStore(state => state.selectedDay);
  const started = useMetadataBuilderStore(state => twoStreams ? state.twoSlotStarted : state.oneSlotStarted);
  const selectedPath = useMetadataEditingStore(state => state.selected);
  const select = useMetadataEditingStore(state => state.select);
  const layout = session?.dayLayouts?.[mode];
  const metadata = session?.projection;
  const artwork = session?.artwork;
  useEffect(() => {
    if (!metadata || !artwork || layout || started) return;
    const startedFlag = twoStreams ? { twoSlotStarted: true } : { oneSlotStarted: true };
    useMetadataBuilderStore.setState({ ...startedFlag, selectedDay: "mon", previewDay: "mon" });
    dispatchBuilderMetadata(createLayout(metadata, artwork));
    select(`days.mon.${mode}.0`);
  }, [metadata, artwork, layout, started, select, mode, twoStreams, createLayout]);
  useEffect(() => {
    const selectedDay = weekdayKeys.find(key => selectedPath?.startsWith(`days.${key}.`));
    if (selectedDay) useMetadataBuilderStore.setState({ selectedDay });
  }, [selectedPath]);
  const selectedElement = layout && layout.mode !== "offline"
    ? (layout.exceptions[day] ?? layout.shared).find((_, index) => selectedPath === `days.${day}.${mode}.${index}`)
    : undefined;
  useEffect(() => {
    if (selectedElement) setSelectedStream(selectedElement.bind.startsWith("slot2.") ? 2 : 1);
  }, [selectedElement]);
  if (!metadata || !artwork) return null;
  if (!layout || layout.mode === "offline") return <Button variant="outline" onClick={() => {
    endMetadataMerge();
    dispatchBuilderMetadata(createLayout(metadata, artwork));
  }}>{t(twoStreams ? "builder.twoStreams.addLayout" : "builder.stream.addLayout")}</Button>;
  const labels = metadataLabels(t);
  const elements = layout.exceptions[day] ?? layout.shared;
  const datesEnabled = dayDateEnabled(metadata);
  const dateSelected = datesEnabled && selectedPath === `days.${day}.date`;
  const selectedIndex = elements.findIndex((_, index) => selectedPath === `days.${day}.${mode}.${index}`);
  const fallbackIndex = elements.findIndex(element => element.bind === `slot${selectedStream}.title`);
  const index = selectedIndex >= 0 ? selectedIndex : fallbackIndex;
  const bindings = streamBindings(selectedStream);
  const requiredBindings = requiredStreamBindings(selectedStream);
  const selectedBind = dateSelected ? "day.date" : elements[index]?.bind;
  return (
    <div className="space-y-3">
      <DayPreviewControls />
      <BuilderStreamInputs twoStreams={twoStreams} />
      <DayLayoutExceptionControls metadata={metadata} layout={layout} day={day} />
      <DayAnchorControls layout={layout} day={day} source={(twoStreams ? artwork.double : artwork.single)[weekdayKeys.indexOf(day)]!} />
      {twoStreams && <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          {([1, 2] as const).map(number => <Button key={number} size="sm" variant={selectedStream === number ? "default" : "outline"} onClick={() => {
            endMetadataMerge();
            setSelectedStream(number);
            const titleIndex = elements.findIndex(element => element.bind === `slot${number}.title`);
            select(titleIndex >= 0 ? `days.${day}.${mode}.${titleIndex}` : null);
          }}>{t("builder.twoStreams.stream", { number })}</Button>)}
        </div>
        <Button variant="outline" size="sm" disabled={!elements.some(element => element.bind === `slot${selectedStream === 1 ? 2 : 1}.title`)} onClick={() => {
          endMetadataMerge();
          const payload = copyStreamLayout(metadata, layout, day, selectedStream);
          dispatchBuilderMetadata(payload);
          if (payload.type !== "day-layout/set" || payload.layout.mode === "offline") return;
          const updated = payload.layout.exceptions[day] ?? payload.layout.shared;
          const titleIndex = updated.findIndex(element => element.bind === `slot${selectedStream}.title`);
          select(titleIndex >= 0 ? `days.${day}.${mode}.${titleIndex}` : null);
        }}>{t("builder.twoStreams.copy", { source: selectedStream === 1 ? 2 : 1, destination: selectedStream })}</Button>
        <p className="text-neutral-500">{t("builder.twoStreams.copyHelp")}</p>
      </div>}
      <div className="grid grid-cols-1 gap-1 min-[480px]:grid-cols-2">
        {bindings.map(bind => {
          const elementIndex = elements.findIndex(element => element.bind === bind);
          const enabled = elementIndex >= 0;
          const required = requiredBindings.includes(bind);
          return <div key={bind} className={cn("flex items-center gap-2 rounded border px-2 py-1", selectedBind === bind ? "border-neutral-900 bg-neutral-100" : "border-neutral-200")}>
            <input type="checkbox" checked={enabled} disabled={required && enabled} title={labels[bind]} onChange={event => {
              endMetadataMerge();
              const payload = toggleStreamElement(metadata, layout, day, bind, event.target.checked);
              dispatchBuilderMetadata(payload);
              if (payload.type !== "day-layout/set" || payload.layout.mode === "offline") return;
              const updated = payload.layout.exceptions[day] ?? payload.layout.shared;
              const index = updated.findIndex(element => element.bind === bind);
              select(index >= 0 ? `days.${day}.${mode}.${index}` : null);
            }} />
            <button type="button" disabled={!enabled} className="min-w-0 flex-1 py-1 text-left disabled:text-neutral-400" onClick={() => {
              endMetadataMerge(); select(`days.${day}.${mode}.${elementIndex}`);
            }}>{labels[bind]}{required && <span className="ml-1 text-neutral-500">{t("builder.stream.required")}</span>}</button>
          </div>;
        })}
        <div className={cn("flex items-center gap-2 rounded border px-2 py-1", dateSelected ? "border-neutral-900 bg-neutral-100" : "border-neutral-200")}>
          <input type="checkbox" checked={datesEnabled} title={t("builder.stream.dayDate")} onChange={event => {
            endMetadataMerge();
            dispatchBuilderMetadata(setDayDateFormat(metadata, event.target.checked ? "%d" : ""));
            select(event.target.checked ? `days.${day}.date` : null);
          }} />
          <button type="button" disabled={!datesEnabled} className="min-w-0 flex-1 py-1 text-left disabled:text-neutral-400" onClick={() => {
            endMetadataMerge(); select(`days.${day}.date`);
          }}>{t("builder.stream.dayDate")}</button>
        </div>
      </div>
      {!streamLayoutComplete(metadata, mode) && <p className="text-red-600">{t(twoStreams ? "builder.twoStreams.required" : "builder.stream.titleRequired")}</p>}
      {dateSelected ? <DayDateEditor metadata={metadata} day={day} /> : elements[index] &&
        <StreamElementEditor key={`${day}:${elements[index]!.bind}`} metadata={metadata} layout={layout} day={day} index={index} />}
    </div>
  );
}
